import {gsap} from "gsap";
import {ISymbol} from "../../core/reel/ReelInterfaces";
import {TheBorder} from "../TheBorder";
import {TheBorderStatics} from "../TheBorderStatics";
import {TheBorderGameAssets} from "../TheBorderGameAssets";
import {ArrayUtils} from "../utils/ArrayUtils";

export enum TheBorderSymbolStates {
    NORMAL,
    SPIN,
    NO_WIN,
    LANDING,
    COLLAPSE_LANDING,
    SPIN_HORIZONTAL,
    SPIN_IN,
    SPIN_OUT
}

export class TheBorderSymbol extends PIXI.Container implements ISymbol {
    get collapseYPos(): number {
        return this._collapseYPos;
    }
    private static SPLIT_NUMBER_CONTAINER_OFFSET: PIXI.Point = new PIXI.Point(54, -49);
    public state: TheBorderSymbolStates;
    protected _newStateSprite: PIXI.Sprite;
    protected _spineAnimation: PIXI.spine.Spine;
    protected _spineContainer: PIXI.Container;
    protected _splitLevel: number = 1;
    private _isSpinOut:boolean;
    private readonly _splitLevelNumber: PIXI.BitmapText;
    private readonly _winAddonSprite: PIXI.Sprite;
    private readonly _darkOverlayContainer: PIXI.Container;
    private readonly _darkOverlayFull: PIXI.Sprite;
    private readonly _darkOverlayHalf: PIXI.Sprite;
    private readonly _topLayerContainer: PIXI.Container;
    private readonly _splitLevelContainer: PIXI.Container;

    constructor() {
        super();
        this._isSpinOut = false;
        this.isRemoved = false;
        this._isUsed = false;
        this._hasInfected = false;
        this._hasNudged = false;
        this._currentStateSprite = new PIXI.Sprite();
        this._currentStateSprite.name = "currentStateSprite";
        this._currentStateSprite.anchor.set(0.5, 0.5);
        this._newStateSprite = new PIXI.Sprite();
        this._newStateSprite.name = "newStateSprite";
        this._newStateSprite.anchor.set(0.5, 0.5);
        this._newStateSprite.alpha = 0;
        this._winAddonSprite = new PIXI.Sprite();
        this._winAddonSprite.name = "winAddonSprite";
        this._winAddonSprite.anchor.set(0.5, 0.5);
        this._winAddonSprite.blendMode = PIXI.BLEND_MODES.ADD;
        this._winAddonSprite.alpha = 0;
        this._darkOverlayFull = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkOverlayFull.tint = 0x000000;
        this._darkOverlayFull.alpha = 0;
        this._darkOverlayFull.width = 120;
        this._darkOverlayFull.height = 100;
        this._darkOverlayFull.anchor.set(0.5, 0.5);
        this._darkOverlayHalf = new PIXI.Sprite(PIXI.Texture.WHITE);
        this._darkOverlayHalf.tint = 0x000000;
        this._darkOverlayHalf.alpha = 0;
        this._darkOverlayHalf.width = 120;
        this._darkOverlayHalf.height = 50;
        this._darkOverlayHalf.anchor.set(0.5, 0.5);
        this._darkOverlayContainer = new PIXI.Container();
        this._darkOverlayContainer.alpha = 0;
        this._darkOverlayContainer.addChild(this._darkOverlayFull);
        this._darkOverlayContainer.addChild(this._darkOverlayHalf);
        this._splitLevelContainer = new PIXI.Container();
        this._splitLevelContainer.visible = false;
        this._splitLevelContainer.position = TheBorderSymbol.SPLIT_NUMBER_CONTAINER_OFFSET;
        this._splitLevelNumber = new PIXI.BitmapText("-1", {
            fontName: TheBorderGameAssets.BITMAP_FONT_SPLIT_SYMBOL_NUMBERS,
            fontSize: 104
        });
        this._splitLevelNumber.anchor = new PIXI.Point(1, 0);
        this._splitLevelContainer.addChild(this._splitLevelNumber);
        this._topLayerContainer = new PIXI.Container();
        this._topLayerContainer.name = "topLayerContainer";
        this._spineContainer = new PIXI.Container();
        this._spineContainer.name = "spineContainer";
        if (this._spineAnimation) {
            this._spineContainer.addChild(this._spineAnimation);
        }
        this._topLayerContainer.addChild(this._spineContainer);

        this.addChild(this._newStateSprite);
        this.addChild(this._currentStateSprite);
        this.addChild(this._winAddonSprite);
        this.addChild(this._splitLevelContainer);
        this.addChild(this._darkOverlayContainer);
    }

    private _hasNudged: boolean = false;

    get hasNudged(): boolean {
        return this._hasNudged;
    }

    set hasNudged(value: boolean) {
        this._hasNudged = value;
    }

    get splitLevelContainer(): PIXI.Container {
        return this._splitLevelContainer;
    }

    private _symbolName: string;

    get symbolName(): string {
        return this._symbolName;
    }

    set symbolName(value: string) {
        this._symbolName = value;
    }

    private _reelPosition: number;

    get reelPosition(): number {
        return this._reelPosition;
    }

    set reelPosition(value: number) {
        this._reelPosition = value;
    }

    private _hasInfected: boolean;

    get hasInfected(): boolean {
        return this._hasInfected;
    }

    set hasInfected(value: boolean) {
        this._hasInfected = value;
    }

    private _reelTargetPosition: number;

    get reelTargetPosition(): number {
        return this._reelTargetPosition;
    }

    set reelTargetPosition(value: number) {
        this._reelTargetPosition = value;
    }

    private _reelId: number;

    get reelId(): number {
        return this._reelId;
    }

    set reelId(value: number) {
        this._reelId = value;
    }

    private _transformed: boolean;

    get transformed(): boolean {
        return this._transformed;
    }

    set transformed(value: boolean) {
        this._transformed = value;
    }

    private _isUsed: boolean;

    get isUsed(): boolean {
        return this._isUsed;
    }

    set isUsed(value: boolean) {
        this._isUsed = value;
    }

    private _sizeMultiplier: number;

    get sizeMultiplier(): number {
        return this._sizeMultiplier;
    }

    set sizeMultiplier(value: number) {
        this._sizeMultiplier = value;
        if (value == 0.5) {
            this._currentStateSprite.position.y = 0 - TheBorderStatics.symbolHeight / 4;
            this._newStateSprite.position.y = 0 - TheBorderStatics.symbolHeight / 4;
            this._winAddonSprite.position.y = 0 - TheBorderStatics.symbolHeight / 4;
            this._darkOverlayHalf.position.y = 0 - TheBorderStatics.symbolHeight / 4;
        } else {
            this._currentStateSprite.position.y = 0;
            this._newStateSprite.position.y = 0;
            this._winAddonSprite.position.y = 0;
        }
    }

    get splitLevel(): number {
        return this._splitLevel;
    }

    set splitLevel(value: number) {
        this._splitLevel = value;
        if (this._splitLevel > 1) {
            this._splitLevelContainer.visible = true;
        }
        this._splitLevelNumber.text = "x" + this._splitLevel;
    }

    protected _isRemoved: boolean;

    get isRemoved(): boolean {
        return this._isRemoved;
    }

    set isRemoved(value: boolean) {
        this._isRemoved = value;
    }

    protected _currentStateSprite: PIXI.Sprite;

    get currentStateSprite(): PIXI.Sprite {
        return this._currentStateSprite;
    }

    private _collapseYPos: number;

    set collapseYPos(value: number) {
        this._collapseYPos = value;
    }

    private _aliasName: string;

    get aliasName(): string {
        return this._aliasName || this._symbolName;
    }

    set aliasName(value: string) {
        this._aliasName = value;
    }

    get winAddonSprite(): PIXI.Sprite {
        this._winAddonSprite.texture = this._currentStateSprite.texture;
        return this._winAddonSprite;
    }

    get topLayerContainer(): PIXI.Container {
        return this._topLayerContainer;
    }

    get darkOverlayContainer(): PIXI.Container {
        return this._darkOverlayContainer;
    }

    public init(initState: TheBorderSymbolStates): TheBorderSymbol {
        this.isRemoved = false;
        this.getStateAnimation(initState);
        return this;
    }

    public hide():void{
        this._currentStateSprite.alpha = 0;
    }

    public show():void{
        this._currentStateSprite.alpha = 1;
    }

    public getSplitAndChangeAnimation(newSplitLevel: number, newSymbol: string): gsap.core.Timeline {
        const splitTL: gsap.core.Timeline = gsap.timeline();
        splitTL.add(() => {
            this.symbolName = newSymbol;
            this.splitLevel = newSplitLevel;
        });
        splitTL.add(this.setState(TheBorderSymbolStates.NORMAL, "", 0.3));
        splitTL.add(() => {
            this.currentStateSprite.alpha = 1;
            this.transformed = true;
        });

        return splitTL;
    }

    public animateCollapse(): gsap.core.Timeline {
        const timeline: gsap.core.Timeline = gsap.timeline();
        timeline.add([
            gsap.to(this.position, {
                //duration : 0.06 * (this.reelTargetPosition - this.reelPosition),
                duration: 0.06 * (this._collapseYPos - this.position.y) / TheBorderStatics.symbolHeight,
                y: this._collapseYPos,
                ease: "power3.in"
            }),
            gsap.to(this._topLayerContainer, {
                duration: 0.06 * (this._collapseYPos - this.position.y) / TheBorderStatics.symbolHeight,
                y: this._collapseYPos,
                ease: "power3.in"
            })
        ]);
        timeline.add([
            gsap.to(this.position, {
                duration: 0.04,
                y: this._collapseYPos - 2,
                ease: "none"
            }),
            gsap.to(this._topLayerContainer, {
                duration: 0.04,
                y: this._collapseYPos - 2,
                ease: "none"
            })
        ]);
        timeline.add([
            gsap.to(this.position, {
                duration: 0.08,
                y: this._collapseYPos,
                ease: "none"
            }),
            gsap.to(this._topLayerContainer, {
                duration: 0.08,
                y: this._collapseYPos,
                ease: "none"
            })
        ]);

        return timeline;
    }

    public getShakeAnimation(duration: number = 1.715): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        const wiggles = ArrayUtils.shuffleArray([TheBorderStatics.bigWinAnticipationWiggle, TheBorderStatics.bigWinAnticipationWiggle2]);
        const x = Math.random() > 0.5 ? "-=2" : "+=2";
        const y = Math.random() > 0.5 ? "-=1" : "+=1";

        tl.add(gsap.to(this.position, {
            duration: duration,
            x: x,
            ease: wiggles[0]
        }));
        tl.add(gsap.to(this.position, {
            duration: duration,
            y: y,
            ease: wiggles[1]
        }), "<");
        tl.add(gsap.to(this.currentStateSprite, {
            duration: duration,
            rotation: 0.1,
            ease: wiggles[1]
        }), "<");

        return tl.reverse();
    }

    public setCurrentStateSpriteTexture(textureName: string = ""): void {
        if (textureName == "none") {
            this._currentStateSprite.texture = PIXI.Texture.EMPTY;
        } else {
            this._currentStateSprite.texture = TheBorder.assetLoader.getTextures(TheBorderGameAssets.getSymbolStateTexturePath(this._symbolName, textureName))[0];
        }
    }

    public setNewStateSpriteTexture(textureName: string = ""): void {
        if (textureName == "none") {
            this._newStateSprite.texture = PIXI.Texture.EMPTY;
        } else {
            this._newStateSprite.texture = TheBorder.assetLoader.getTextures(TheBorderGameAssets.getSymbolStateTexturePath(this._symbolName, textureName))[0];
        }
    }

    public getStateAnimation(state: TheBorderSymbolStates, changeSpeed: number = 0, newStateAlpha: number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();

        if (this.state == state) {
            return tl;
        }

        if (this._symbolName !== "S") {
            if ((state == TheBorderSymbolStates.SPIN_IN || state == TheBorderSymbolStates.SPIN_OUT)) {
                state = TheBorderSymbolStates.SPIN;
            }
            if (state == TheBorderSymbolStates.COLLAPSE_LANDING) {
                state = TheBorderSymbolStates.LANDING;
            }
        }

        switch (state) {
            case TheBorderSymbolStates.NORMAL:
                tl.add(this.setState(state, ""));
                break;
            case TheBorderSymbolStates.SPIN:
                let textureNameSpin: string = "_spin";
                tl.add(this.setState(state, textureNameSpin, changeSpeed, newStateAlpha));
                break;
            case TheBorderSymbolStates.LANDING:
                tl.add(this.setState(state, "", changeSpeed));
                break;
            case TheBorderSymbolStates.COLLAPSE_LANDING:
                tl.add(this.setState(state, "_land", changeSpeed));
                break;
            default:

        }

        if (tl.duration() == 0) {
            tl.add(gsap.to(this, {duration: 0.0001}));
        }

        return tl;
    }

    public getNoWinAnimation(duration: number): gsap.core.Timeline {
        if (this.symbolName.indexOf("_H") > -1) {
            this._darkOverlayHalf.alpha = 1;
            this._darkOverlayFull.alpha = 0;
        } else {
            this._darkOverlayHalf.alpha = 0;
            this._darkOverlayFull.alpha = 1;
        }
        const noWinTL: gsap.core.Timeline = gsap.timeline();
        noWinTL.add([
            gsap.to(this._darkOverlayContainer, {duration: duration, alpha: 0.55}),
            gsap.to(this._spineContainer, {duration: duration, alpha: 0}),
            gsap.to(this.currentStateSprite, {duration: duration, alpha: 0.9})
        ]);
        return noWinTL;
    }

    public getNoWinEndAnimation(): gsap.core.Timeline {
        const noWinEndTL: gsap.core.Timeline = gsap.timeline();
        noWinEndTL.add([
            gsap.to(this._darkOverlayContainer, {duration: 0.1, alpha: 0}),
            gsap.to(this._spineContainer, {duration: 0.1, alpha: 1}),
            gsap.to(this.currentStateSprite, {duration: 0.1, alpha: 1})
        ]);
        return noWinEndTL;
    }

    public reset(): void {
        this._isSpinOut = false;
        this.isUsed = false;
        this._splitLevel = 1;
        this._sizeMultiplier = 1;
        this._splitLevelContainer.visible = false;
        this._newStateSprite.alpha = 0;
        this._winAddonSprite.alpha = 0;
        this._darkOverlayFull.alpha = 0;
        this._darkOverlayHalf.alpha = 0;
        this._currentStateSprite.alpha = 1;
        this._currentStateSprite.position.y = 0;
        this._newStateSprite.position.y = 0;
        this._winAddonSprite.position.y = 0;
        this.addChild(this.topLayerContainer);
        (<TheBorderSymbol>this).setState(TheBorderSymbolStates.NORMAL, "");
        this._aliasName = "";
        this._hasInfected = false;
        this._hasNudged = false;
    }

    public changeToWild(): void {
        this._aliasName = "WR";

        this.setCurrentStateSpriteTexture("_W");
        this.setNewStateSpriteTexture("_W");

        this._currentStateSprite.texture = this._newStateSprite.texture;
        this._currentStateSprite.alpha = 1;
        this._newStateSprite.alpha = 0;
    }

    private setState(state: TheBorderSymbolStates, textureName: string, changeSpeed: number = 0, newStateAlpha: number = 1): gsap.core.Timeline {
        const tl: gsap.core.Timeline = gsap.timeline();
        tl.add(() => {
            this.state = state;
            this.setNewStateSpriteTexture(textureName);
        });
        tl.add([
            gsap.to(this._currentStateSprite, {
                duration: changeSpeed,
                alpha: 0,
                ease: "power2.in"
            }),
            gsap.to(this._newStateSprite, {
                duration: changeSpeed,
                alpha: newStateAlpha,
                ease: "power2.out"
            })
        ]);
        tl.add(() => {
            this._currentStateSprite.texture = this._newStateSprite.texture;
            this._currentStateSprite.alpha = 1;
            this._newStateSprite.alpha = 0;
        });

        return tl;
    }

    get isSpinOut():boolean {
        return this._isSpinOut;
    }

    set isSpinOut(value:boolean) {
        this._isSpinOut = value;
    }
}